import React, { useEffect, useState } from 'react';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { useDispatch } from "react-redux";
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { ReportRecipientType, ReportType } from '@shared/models/report.models';
import { getReportConfigs, updateReportConfig } from '@features/settings/services/ReportService';
import { GenericDropDownList } from "../../molecules/GenericDropDownList";
import { SendSampleReportButton } from "./SendSampleReportButton";

export function MonthlyWhereaboutsSummarySetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipientType, setRecipientType] = useState<ReportRecipientType>(ReportRecipientType.COMPANY_ADMINS);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    getReportConfigs()
      .then(configs => {
        if (!active) {
          return;
        }
        const config = configs.find(c => c.reportType === ReportType.MONTHLY_WHEREABOUTS_SUMMARY);
        if (config) {
          setEnabled(config.enabled);
          if (config.recipientTypes && config.recipientTypes.length > 0) {
            setRecipientType(config.recipientTypes[0]);
          }
        }
      })
      .catch(() => { /* leave defaults in place */ });
    return () => { active = false; };
  }, []);

  const persist = async (next: { enabled: boolean; recipientType: ReportRecipientType }) => {
    try {
      setIsLoading(true);
      await updateReportConfig({
        reportType: ReportType.MONTHLY_WHEREABOUTS_SUMMARY,
        enabled: next.enabled,
        recipientTypes: [next.recipientType],
      });
      setEnabled(next.enabled);
      setRecipientType(next.recipientType);
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onToggle = (value: boolean) => persist({ enabled: value, recipientType });

  const onRecipientChange = (value: ReportRecipientType) => {
    if (value === recipientType) {
      return;
    }
    persist({ enabled, recipientType: value });
  };

  const recipientItems = [
    { id: ReportRecipientType.COMPANY_ADMINS, name: t('settings.whereabouts-summary.recipients.company-admins') },
    { id: ReportRecipientType.TEAM_ADMINS, name: t('settings.whereabouts-summary.recipients.team-admins') },
    { id: ReportRecipientType.LINE_MANAGERS, name: t('settings.whereabouts-summary.recipients.line-managers') },
  ];

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.whereabouts-summary.title')}</BodyLarge>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onToggle} />}
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.whereabouts-summary.subtitle')}</BodyRegular>
      <SendSampleReportButton reportType={ReportType.MONTHLY_WHEREABOUTS_SUMMARY} />

      {enabled && (
        <>
          <BodyRegular weight={600} style={{ marginBottom: 8 }}>{t('settings.whereabouts-summary.recipients.label')}</BodyRegular>
          <GenericDropDownList selectedId={recipientType}
                               items={recipientItems}
                               placeholder={'settings.whereabouts-summary.recipients.placeholder'}
                               disabled={isLoading}
                               onSelect={onRecipientChange} />
        </>
      )}
    </ConfigurationSection>
  );
}
